import React from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { FiCheck } from 'react-icons/fi';

function ExecutionPanel({ data }) {
  const { t } = useTranslation();

  // Get translated content
  const title = t('executionFlow.title', { defaultValue: 'How HADEN Executes Build-to-Specification Programs' });
  const subtitle = t('executionFlow.subtitle', { defaultValue: 'Programs are executed with disciplined translation of requirements into design controls, build controls, and verification evidence — so acceptance is predictable, not last-minute.' });

  // Get translated steps
  const translatedSteps = t('executionFlow.steps', { returnObjects: true });

  // Default execution flow with rich content
  const defaultExecutionFlow = [
    {
      step: "Requirement Intake & Decomposition",
      description: "Customer specifications are decomposed into actionable engineering requirements, interfaces, constraints, and acceptance criteria.",
      signals: [
        "Derived requirements + constraints captured",
        "Clarifications and assumptions controlled",
        "Requirement-to-test intent established early"
      ]
    },
    {
      step: "Design Translation & Control",
      description: "Requirements are translated into architecture, component selection, controls logic, and integration design — including program-dependent military engineering considerations.",
      signals: [
        "Interface discipline + integration planning",
        "Environmental/EMI/EMC/RAM thinking applied as applicable",
        "Design reviews tied to requirement closure"
      ]
    },
    {
      step: "Controlled Build & Integration",
      description: "Systems are built with controlled processes, traceable assembly, and integration discipline — enabling predictable rework control and repeatability.",
      signals: [
        "Configuration discipline and build traceability",
        "Wiring/harnessing and workmanship control",
        "Serviceability and maintainability built-in"
      ]
    },
    {
      step: "Verification & Acceptance Readiness",
      description: "Verification is executed with evidence-first thinking: test plans, FAT setups, witness readiness, and documented results aligned to acceptance criteria.",
      signals: [
        "ATP/TP and FAT readiness packages",
        "Traceable evidence packs for closure",
        "Customer witness testing support"
      ]
    },
    {
      step: "Delivery Support & Lifecycle Responsiveness",
      description: "Delivery is supported with deployment-ready documentation and responsive engineering support for field feedback, upgrades, and sustainment needs.",
      signals: [
        "Documentation handover and controlled updates",
        "Spares/serviceability support planning",
        "Controlled change handling (program dependent)"
      ]
    }
  ];

  // Use translated steps if available, otherwise defaults
  const steps = Array.isArray(translatedSteps) ? translatedSteps.map((step, i) => ({
    step: step.title || defaultExecutionFlow[i]?.step,
    description: step.description || defaultExecutionFlow[i]?.description,
    signals: step.points || defaultExecutionFlow[i]?.signals
  })) : defaultExecutionFlow;

  return (
    <div 
      id="execution" 
      className="panel panel-white scroll-mt-20" 
      data-testid="execution-panel"
    >
      <div className="panel-content">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className="text-center mb-10"
        >
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4 heading-premium">
            {title}
          </h2>
          <p className="text-base md:text-lg max-w-4xl mx-auto subheading-premium">
            {subtitle}
          </p>
        </motion.div>

        <div className="max-w-4xl mx-auto">
          {steps.map((item, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, ease: "easeOut", delay: index * 0.08 }}
              className="relative mb-8 last:mb-0"
              data-testid={`execution-step-${index}`}
            >
              {/* Connector line */}
              {index < steps.length - 1 && (
                <div 
                  className="absolute left-6 top-14 w-0.5 h-full -mb-8"
                  style={{ backgroundColor: 'rgba(16, 25, 87, 0.12)' }}
                />
              )}

              <div className="flex items-start gap-5">
                {/* Step number */}
                <div 
                  className="flex-shrink-0 w-12 h-12 rounded-full text-white flex items-center justify-center font-bold text-lg"
                  style={{ backgroundColor: '#101957' }}
                >
                  {index + 1}
                </div>

                {/* Content */}
                <div className="flex-1 card-premium p-6">
                  <h3 className="text-xl font-bold mb-2" style={{ color: '#101957' }}>
                    {item.step}
                  </h3>
                  <p className="text-sm mb-4 leading-relaxed" style={{ color: '#475569' }}>
                    {item.description}
                  </p>

                  {/* Signals */}
                  {item.signals && item.signals.length > 0 && (
                    <div className="space-y-2">
                      {item.signals.slice(0, 3).map((signal, signalIndex) => (
                        <div 
                          key={signalIndex}
                          className="flex items-center gap-2 text-sm"
                          style={{ color: '#64748b' }}
                        >
                          <FiCheck 
                            className="flex-shrink-0 text-xs" 
                            style={{ color: '#101957' }} 
                          />
                          <span>{signal}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default ExecutionPanel;
